import React from 'react';
import { MapContainer, TileLayer, Marker, Popup, Circle } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { Box, Typography, Chip, Divider, Paper } from '@mui/material';
import StarIcon from '@mui/icons-material/Star';
import LocationOnIcon from '@mui/icons-material/LocationOn';

// Modern company marker with star icon and pulse effect
const createCompanyMarker = () => {
  return L.divIcon({
    className: 'custom-marker company-marker',
    html: `
      <div class="marker-container">
        <div style="background-color: #10B981; color: white; border-radius: 50%; width: 46px; height: 46px; display: flex; align-items: center; justify-content: center; font-size: 18px; border: 3px solid white; box-shadow: 0 4px 6px rgba(0, 0, 0, 0.15); animation: pulse 2s infinite;">
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="white"><path d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z"/></svg>
        </div>
        <div style="position: absolute; left: 50%; transform: translateX(-50%); bottom: -24px; white-space: nowrap; background-color: rgba(16, 185, 129, 0.9); color: white; padding: 4px 8px; border-radius: 12px; font-weight: bold; font-size: 10px; box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);">Ihr Unternehmen</div>
      </div>
    `,
    iconSize: [46, 46],
    iconAnchor: [23, 46],
    popupAnchor: [0, -46],
  });
};

// Modern rank marker with gradient based on rank
const createRankMarker = (rank) => {
  // Generate gradient based on rank
  let gradientColor;
  if (rank <= 3) {
    gradientColor = 'linear-gradient(135deg, #10B981 0%, #059669 100%)';
  } else if (rank <= 10) {
    gradientColor = 'linear-gradient(135deg, #F59E0B 0%, #D97706 100%)';
  } else {
    gradientColor = 'linear-gradient(135deg, #EF4444 0%, #DC2626 100%)';
  }
  
  return L.divIcon({
    className: 'custom-marker rank-marker',
    html: `
      <div style="background: ${gradientColor}; color: white; border-radius: 50%; width: 36px; height: 36px; display: flex; align-items: center; justify-content: center; font-size: 15px; font-weight: bold; border: 2px solid white; box-shadow: 0 3px 5px rgba(0, 0, 0, 0.2); text-align: center; transition: all 0.3s ease;">
        ${rank > 20 ? '20+' : rank}
      </div>
    `,
    iconSize: [36, 36],
    iconAnchor: [18, 18],
  });
};

const LocationMap = ({ companyLocation, companyName, locationRanks }) => {
  const center = companyLocation ? [companyLocation.lat, companyLocation.lng] : [51.1657, 10.4515]; // Default to Germany center

  // Function to get color based on rank
  const getColorForRank = (rank) => {
    if (rank <= 3) return '#10B981';  // Green for top 3
    if (rank <= 10) return '#F59E0B'; // Orange for up to position 10
    return '#EF4444';                 // Red for 11+ (or error)
  };

  // Function for circle opacity based on rank
  const getOpacityForRank = (rank) => {
    if (rank <= 3) return 0.2;       // Top 3 - more visible
    if (rank <= 10) return 0.15;     // Up to 10 - medium visibility
    return 0.1;                      // 11+ - subtle background
  };

  // Function for radius (in meters) based on rank
  const getRadiusForRank = (rank) => {
    if (rank <= 3) return 800;       // Top 3 - larger area of influence
    if (rank <= 10) return 600;      // Up to 10 - medium area of influence
    return 400;                      // 11+ - small area of influence
  };

  // Status label based on rank
  const getRankStatus = (rank) => {
    if (rank <= 3) return { text: 'Top Position', color: '#10B981' };
    if (rank <= 10) return { text: 'Gute Position', color: '#F59E0B' };
    return { text: 'Optimierungsbedarf', color: '#EF4444' };
  };

  return (
    <MapContainer
      center={center}
      zoom={11}
      style={{ height: '500px', width: '100%', borderRadius: '12px', boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)' }}
    >
      <TileLayer
        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        attribution='© <a href="http://osm.org/copyright">OpenStreetMap</a> contributors'
      />
      
      {/* Concentric circles around the center for better visualization */}
      {companyLocation && (
        <>
          <Circle
            center={[companyLocation.lat, companyLocation.lng]}
            radius={5000}
            pathOptions={{ color: '#6366F1', fillColor: '#6366F1', weight: 1, opacity: 0.3, fillOpacity: 0.05 }}
          />
          <Circle
            center={[companyLocation.lat, companyLocation.lng]}
            radius={10000}
            pathOptions={{ color: '#6366F1', fillColor: '#6366F1', weight: 1, opacity: 0.2, fillOpacity: 0.03 }}
          />
          <Circle
            center={[companyLocation.lat, companyLocation.lng]}
            radius={15000}
            pathOptions={{ color: '#6366F1', fillColor: '#6366F1', weight: 1, opacity: 0.1, fillOpacity: 0.02 }}
          />
        </>
      )}
      
      {/* Company marker */}
      {companyLocation && (
        <Marker
          position={[companyLocation.lat, companyLocation.lng]}
          icon={createCompanyMarker()}
        >
          <Popup className="custom-popup">
            <Paper elevation={0} sx={{ p: 1.5, borderRadius: 2, maxWidth: 320 }}>
              <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 1 }}>
                <Typography variant="h6" sx={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <StarIcon sx={{ color: '#10B981' }} />
                  {companyName}
                </Typography>
              </Box>
              <Divider sx={{ my: 1 }} />
              <Typography variant="body2" sx={{ fontWeight: 500, mb: 1 }}>
                Ihr Unternehmensstandort
              </Typography>
              <Chip 
                label="Referenzpunkt für Rankings" 
                size="small" 
                color="secondary" 
                sx={{ fontSize: '0.75rem', mt: 1 }} 
              />
            </Paper>
          </Popup>
        </Marker>
      )}
      
      {/* Rank markers for the different locations */}
      {locationRanks.map((loc, index) => (
        <React.Fragment key={index}>
          <Circle
            center={[loc.lat, loc.lng]}
            radius={getRadiusForRank(loc.rank)}
            pathOptions={{ 
              fillColor: getColorForRank(loc.rank),
              color: getColorForRank(loc.rank),
              fillOpacity: getOpacityForRank(loc.rank),
              weight: 1,
            }}
          />
          <Marker
            position={[loc.lat, loc.lng]}
            icon={createRankMarker(loc.rank)}
          >
            <Popup className="custom-popup">
              <Paper elevation={0} sx={{ p: 2, borderRadius: 2, maxWidth: 320 }}>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 1 }}>
                  <Typography variant="h6" sx={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <LocationOnIcon sx={{ color: getColorForRank(loc.rank) }} />
                    {loc.locationName || 'Unbekannter Ort'}
                  </Typography>
                </Box>
                
                <Chip 
                  label={getRankStatus(loc.rank).text}
                  size="small"
                  sx={{ 
                    bgcolor: `${getRankStatus(loc.rank).color}20`, 
                    color: getRankStatus(loc.rank).color,
                    fontWeight: 500,
                    mb: 2,
                    mt: 0.5
                  }} 
                />
                
                <Divider sx={{ my: 1.5 }} />
                
                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                  Ranking für diesen Standort: <strong>#{loc.rank}</strong>
                </Typography>
                
                {loc.companies && loc.companies.length > 0 && (
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                      Top-Wettbewerber in dieser Gegend:
                    </Typography>
                    {loc.companies.slice(0, 3).map((company, idx) => (
                      <Box key={idx} sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                        <Chip 
                          label={`#${company.rank}`} 
                          size="small" 
                          sx={{ 
                            mr: 1, 
                            minWidth: 40, 
                            bgcolor: idx === 0 ? '#10B98120' : 'grey.100',
                            color: idx === 0 ? '#10B981' : 'text.secondary'
                          }} 
                        />
                        <Typography variant="body2" noWrap sx={{ flex: 1 }}>
                          {company.name}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                )}
              </Paper>
            </Popup>
          </Marker>
        </React.Fragment>
      ))}
    </MapContainer>
  );
};

export default LocationMap;